import React from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";

import MessagePreviewScreen from "../../../../shared/components/common/ui/MessagePreviewScreen/MessagePreviewScreen";
import { previewEmptySx } from "../CampaignViewPage.styles";

export default function CampaignTemplatePreviewContent({
  hasConfiguredTemplates,
  isLoading,
  selectedTemplatePreview,
  campaignTemplatePreviews = [],
  activeTemplateId = false,
  onSelectTemplate,
}) {
  const channelButtons = [
    { key: "whatsapp", label: "WhatsApp" },
    { key: "email", label: "E-mail" },
    { key: "sms", label: "SMS" },
  ].reduce((buttons, channel) => {
    const template = campaignTemplatePreviews.find((item) => {
        const normalizedChannel = String(item.channel || "").toLowerCase();

        if (channel.key === "whatsapp") {
          return normalizedChannel === "whatsapp" ||
            normalizedChannel === "whatsapp_api" ||
            normalizedChannel === "whatsapp_web";
        }

        return normalizedChannel === channel.key;
      });

    if (!template) {
      return buttons;
    }

    buttons.push({
      ...channel,
      templateId: template.templateId,
      isActive: template.templateId === activeTemplateId,
    });

    return buttons;
  }, []);

  if (!hasConfiguredTemplates) {
    return (
      <Box sx={previewEmptySx}>
        <Typography color="text.secondary">
          Nenhum modelo vinculado a esta campanha.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          position: "relative",
          height: "100%",
          minHeight: 0,
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #dde3ec",
          backgroundColor: "#f8fafc",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 3,
          }}
        >
          {[0, 1, 2].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              width={96}
              height={28}
              sx={{ borderRadius: "999px", bgcolor: "#e2e8f0" }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            pt: "58px",
            px: 2,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Skeleton
            variant="rounded"
            height={72}
            sx={{ borderRadius: "10px", bgcolor: "#e2e8f0" }}
          />
          <Skeleton
            variant="rounded"
            height={44}
            width="82%"
            sx={{ borderRadius: "10px", alignSelf: "flex-end", bgcolor: "#e2e8f0" }}
          />
          <Skeleton
            variant="rounded"
            height={96}
            sx={{ borderRadius: "10px", bgcolor: "#e2e8f0" }}
          />
          <Skeleton
            variant="rounded"
            height={38}
            width="68%"
            sx={{ borderRadius: "10px", bgcolor: "#e2e8f0" }}
          />
        </Box>
      </Box>
    );
  }

  const hasPreviewContent = Boolean(selectedTemplatePreview?.messages?.length);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid #dde3ec",
        backgroundColor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 3,
        }}
      >
        {channelButtons.map((channel) => (
          <Button
            key={channel.key}
            size="small"
            variant={channel.isActive ? "contained" : "outlined"}
            onClick={() => channel.templateId && onSelectTemplate?.(channel.templateId)}
            sx={{
              borderRadius: "999px",
              minWidth: 96,
              px: 1.5,
              py: 0.4,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              borderColor: "rgba(148, 163, 184, 0.45)",
              backgroundColor: channel.isActive
                ? "#ffffff"
                : "#ffffff",
              color: channel.isActive ? "#1d4ed8" : "#334155",
              "&:hover": {
                backgroundColor: "#ffffff",
                borderColor: "rgba(100, 116, 139, 0.55)",
              },
              ...(channel.isActive && {
                borderColor: "#1d4ed8",
              }),
            }}
          >
            {channel.label}
          </Button>
        ))}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {selectedTemplatePreview && hasPreviewContent ? (
          <MessagePreviewScreen
            disabledButtons
            channel={selectedTemplatePreview.channel}
            messages={selectedTemplatePreview.messages}
            hideMediaConfigMenu
            contentInsetTop={44}
          />
        ) : (
          <Box
            sx={{
              ...previewEmptySx,
              height: "100%",
              pt: "64px",
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography color="text.secondary">
              {selectedTemplatePreview
                ? "O modelo selecionado não possui conteúdo para pré-visualizar."
                : "Não foi possível localizar o modelo selecionado."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
